// Routing Map — a geographic view of the network (ported from LogiCast's Network
// Map + Routing Map). Plots hubs and branches by lat/lng on a pure-SVG US canvas
// and draws the routing edges OpsPanther can derive:
//   • Hub → branch spokes  — width by the branch's forecast for the date
//     (hub volume × the branch's day-of-week share).
//   • Hub ↔ hub transit lanes — every hub pair, colored by estimated transit days
//     (haversine → transitDays); the selected hub's lanes are highlighted.
//   • Reroute lanes — bold source → destination flows where a hub event displaces
//     volume on the date (OpsPanther's only steady inter-hub movement).
// Geography is visualization-only; routing comes from allocations + shares + events.

import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../auth/AuthProvider";
import { Card, Field, Input, Select } from "../../components/ui";
import { getErrorMessage } from "../../lib/errors";
import { fmtN, today } from "../../lib/format";
import { haversine, transitDays } from "../../lib/geo";
import type { Branch, Customer, Hub, HubEvent } from "../../lib/types";
import { forecastHubDay, reroutedInToHub, reroutedOutOfHub } from "../../lib/forecast/engine";
import { branchShares, forecastBranchDay } from "../../lib/forecast/branchForecast";
import { listCustomers } from "../customers/customersApi";
import { listHubs } from "../hubs/hubsApi";
import { listBranches } from "../branches/branchesApi";
import { listHubEvents } from "../hubevents/hubEventsApi";
import { buildOverrideMap, listAccountOverrides, type ForecastOverride } from "../forecast/overridesApi";
import { listAccountBranchActuals, type BranchActual } from "../actuals/branchActualsApi";
import {
  listAccountDirectLanes,
  isDirectActiveOnDate,
  revertDirectLane,
  deleteDirectLane,
} from "./directLanesApi";
import LaneEditorModal from "./LaneEditorModal";
import type { DirectLane } from "../../lib/types";

const ALL = "__all__";
const W = 900;
const H = 520;
const PAD = 16;
const dowOf = (d: string) => new Date(d + "T12:00:00").getDay();
/** Compact volume label for tight matrix cells (e.g. 12,340 -> "12.3K"). */
const fmtK = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}K` : String(Math.round(n)));
const HUB_PALETTE = ["#0f766e", "#0369a1", "#7c3aed", "#c2410c", "#15803d", "#be185d", "#0891b2", "#a16207", "#4338ca", "#db2777"];
const laneColor = (d: number) => (d <= 1 ? "#059669" : d === 2 ? "#0f766e" : d === 3 ? "#0d9488" : "#dc2626");

// Equirectangular projection over the continental US (matches LogiCast bounds).
function toXY(lat: number, lng: number): [number, number] {
  const x = ((lng - -128) / (-65 - -128)) * (W - 2 * PAD) + PAD;
  const y = ((lat - 50) / (24 - 50)) * (H - 2 * PAD) + PAD;
  return [x, y];
}

export default function RoutingMapPage() {
  const { activeAccount } = useAuth();
  const accountId = activeAccount?.accountId ?? null;

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [hubs, setHubs] = useState<Hub[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [overrides, setOverrides] = useState<ForecastOverride[]>([]);
  const [events, setEvents] = useState<HubEvent[]>([]);
  const [branchActuals, setBranchActuals] = useState<BranchActual[]>([]);
  const [directLanes, setDirectLanes] = useState<DirectLane[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [view, setView] = useState<"matrix" | "geo">("matrix");
  const [hubId, setHubId] = useState(ALL);
  const [date, setDate] = useState(today());
  const [editPair, setEditPair] = useState<{ origin: Hub; dest: Hub } | null>(null);
  const [show, setShow] = useState({ spokes: true, lanes: true, reroute: true, direct: true });

  useEffect(() => {
    if (!accountId) return;
    Promise.all([listCustomers(accountId), listHubs(accountId), listBranches(accountId), listAccountOverrides(accountId), listHubEvents(accountId)])
      .then(([c, h, b, ov, ev]) => {
        setCustomers(c);
        setHubs(h.filter((x) => x.active));
        setBranches(b.filter((x) => x.active));
        setOverrides(ov);
        setEvents(ev);
      })
      .catch((e) => setError(getErrorMessage(e, "Failed to load network data.")));
  }, [accountId]);

  const reloadDirectLanes = useMemo(
    () => () => {
      if (!accountId) return;
      listAccountDirectLanes(accountId).then(setDirectLanes).catch(() => {});
    },
    [accountId]
  );
  useEffect(() => reloadDirectLanes(), [reloadDirectLanes]);

  useEffect(() => {
    if (!accountId) return;
    const start = new Date(date + "T12:00:00");
    start.setDate(start.getDate() - 28);
    listAccountBranchActuals(accountId, start.toISOString().slice(0, 10), date)
      .then(setBranchActuals)
      .catch(() => {});
  }, [accountId, date]);

  const overrideMap = useMemo(() => buildOverrideMap(overrides), [overrides]);
  const shares = useMemo(() => branchShares(branches, branchActuals), [branches, branchActuals]);
  const hubColor = useMemo(() => {
    const sorted = [...hubs].sort((a, b) => a.id.localeCompare(b.id));
    return new Map(sorted.map((h, i) => [h.id, HUB_PALETTE[i % HUB_PALETTE.length]]));
  }, [hubs]);

  const hubVol = useMemo(() => {
    const m = new Map<string, number>();
    for (const h of hubs) m.set(h.id, forecastHubDay(customers, h.id, date, overrideMap, events) + reroutedInToHub(customers, h.id, date, events));
    return m;
  }, [hubs, customers, date, overrideMap, events]);

  const geoHubs = useMemo(() => hubs.filter((h) => h.lat != null && h.lng != null), [hubs]);
  const geoBranches = useMemo(() => branches.filter((b) => b.lat != null && b.lng != null), [branches]);
  const missingCoords = hubs.length - geoHubs.length + (branches.length - geoBranches.length);

  const branchVol = useMemo(() => {
    const m = new Map<string, number>();
    const dow = dowOf(date);
    for (const b of geoBranches) {
      const sh = shares.get(b.id)?.shareByDow[dow] ?? 0;
      m.set(b.id, Math.round(forecastBranchDay(hubVol.get(b.hubId) ?? 0, sh)));
    }
    return m;
  }, [geoBranches, shares, hubVol, date]);

  const maxHubVol = useMemo(() => Math.max(1, ...geoHubs.map((h) => hubVol.get(h.id) ?? 0)), [geoHubs, hubVol]);
  const maxBranchVol = useMemo(() => Math.max(1, ...[...branchVol.values()]), [branchVol]);

  // Transit lanes: every hub pair, colored by estimated transit days.
  const transitLanes = useMemo(() => {
    const out: Array<{ a: Hub; b: Hub; x1: number; y1: number; x2: number; y2: number; miles: number; d: number }> = [];
    for (let i = 0; i < geoHubs.length; i++) {
      for (let j = i + 1; j < geoHubs.length; j++) {
        const a = geoHubs[i];
        const b = geoHubs[j];
        const miles = Math.round(haversine(a.lat!, a.lng!, b.lat!, b.lng!));
        const [x1, y1] = toXY(a.lat!, a.lng!);
        const [x2, y2] = toXY(b.lat!, b.lng!);
        out.push({ a, b, x1, y1, x2, y2, miles, d: transitDays(miles) });
      }
    }
    return out;
  }, [geoHubs]);

  // Transit lookup (symmetric) keyed "a|b" → { miles, d } for the matrix view.
  const transitLookup = useMemo(() => {
    const m = new Map<string, { miles: number; d: number }>();
    for (const l of transitLanes) {
      m.set(`${l.a.id}|${l.b.id}`, { miles: l.miles, d: l.d });
      m.set(`${l.b.id}|${l.a.id}`, { miles: l.miles, d: l.d });
    }
    return m;
  }, [transitLanes]);

  const branchesByHub = useMemo(() => {
    const m = new Map<string, number>();
    for (const b of branches) m.set(b.hubId, (m.get(b.hubId) ?? 0) + 1);
    return m;
  }, [branches]);
  const destBranchesOf = useMemo(() => {
    const m = new Map<string, Branch[]>();
    for (const b of branches) {
      const arr = m.get(b.hubId) ?? [];
      arr.push(b);
      m.set(b.hubId, arr);
    }
    return m;
  }, [branches]);

  // Direct-lane model: a branch is local to its home hub UNLESS a direct lane is
  // active on the date, which re-homes it to the origin hub (its volume leaves the
  // destination hub and goes direct origin→branch).
  const directOnDate = useMemo(
    () => directLanes.filter((l) => !l.customerId && isDirectActiveOnDate(l, date)),
    [directLanes, date]
  );
  const branchDirectOrigin = useMemo(() => {
    const m = new Map<string, string>(); // branchId -> origin hub feeding it (any routing)
    for (const l of directOnDate) m.set(l.branchId, l.originHubId);
    return m;
  }, [directOnDate]);
  // Green geo lines: DIRECT lanes only (origin → branch). H2H is drawn origin → home hub.
  const directSet = useMemo(() => {
    const s = new Set<string>();
    for (const l of directOnDate) if (l.routing === "direct") s.add(`${l.originHubId}|${l.branchId}`);
    return s;
  }, [directOnDate]);
  const branchDirectSet = useMemo(() => {
    const s = new Set<string>(); // branches truly bypassing their home hub (direct)
    for (const l of directOnDate) if (l.routing === "direct") s.add(l.branchId);
    return s;
  }, [directOnDate]);
  const h2hPairs = useMemo(() => {
    const set = new Set<string>(); // "origin|homeHub" for h2h lanes
    for (const l of directOnDate) {
      if (l.routing !== "h2h") continue;
      const b = branches.find((x) => x.id === l.branchId);
      if (b) set.add(`${l.originHubId}|${b.hubId}`);
    }
    return [...set].map((k) => k.split("|") as [string, string]);
  }, [directOnDate, branches]);

  // Per (origin|destHub): volume + branch counts fed from that origin (direct & h2h).
  const directByPair = useMemo(() => {
    const vol = new Map<string, number>();
    const cnt = new Map<string, number>();
    const dir = new Map<string, number>();
    for (const l of directOnDate) {
      const b = branches.find((x) => x.id === l.branchId);
      if (!b) continue;
      const k = `${l.originHubId}|${b.hubId}`;
      vol.set(k, (vol.get(k) ?? 0) + (branchVol.get(l.branchId) ?? 0));
      cnt.set(k, (cnt.get(k) ?? 0) + 1);
      if (l.routing === "direct") dir.set(k, (dir.get(k) ?? 0) + 1);
    }
    return { vol, cnt, dir };
  }, [directOnDate, branches, branchVol]);

  // Local volume kept (sorted) at each hub = its branches not fed from elsewhere.
  const localVol = useMemo(() => {
    const m = new Map<string, number>();
    for (const b of geoBranches) {
      if (branchDirectOrigin.has(b.id)) continue;
      m.set(b.hubId, (m.get(b.hubId) ?? 0) + (branchVol.get(b.id) ?? 0));
    }
    return m;
  }, [geoBranches, branchDirectOrigin, branchVol]);

  const activeDirectTotal = directOnDate.length;

  // Reroute lanes: a hub event that displaces volume to other hubs on the date.
  const rerouteLanes = useMemo(() => {
    const out: Array<{ from: string; to: string; vol: number }> = [];
    for (const ev of events) {
      if (ev.active === false || !(ev.startDate <= date && ev.endDate >= date)) continue;
      if (!ev.redistributeVolume || !ev.redistributeHubs?.length) continue;
      const totalOut = reroutedOutOfHub(customers, ev.hubId, date, [ev]);
      if (totalOut <= 0) continue;
      const sumPct = ev.redistributeHubs.reduce((s, r) => s + (r.pct || 0), 0) || 1;
      for (const r of ev.redistributeHubs) {
        const vol = Math.round((totalOut * (r.pct || 0)) / sumPct);
        if (vol > 0) out.push({ from: ev.hubId, to: r.hubId, vol });
      }
    }
    return out;
  }, [events, customers, date]);
  const maxReroute = useMemo(() => Math.max(1, ...rerouteLanes.map((l) => l.vol)), [rerouteLanes]);

  if (!accountId) {
    return (
      <Card>
        <p className="text-sm text-slate-500">No active account. Try signing out and back in.</p>
      </Card>
    );
  }

  const hubXY = (id: string): [number, number] | null => {
    const h = geoHubs.find((x) => x.id === id);
    return h ? toXY(h.lat!, h.lng!) : null;
  };
  const dim = (id: string) => (hubId !== ALL && id !== hubId ? 0.18 : 1);
  const involves = (a: string, b: string) => hubId === ALL || a === hubId || b === hubId;
  const totalBranchVol = [...branchVol.values()].reduce((s, v) => s + v, 0);

  return (
    <div className="space-y-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-lg font-semibold text-slate-900">Routing Map</h1>
        <span className="text-xs text-slate-400">{geoHubs.length} hubs · {geoBranches.length} branches</span>
      </div>
      <div className="flex gap-1 rounded-lg bg-slate-100 p-1 text-sm w-fit">
        {([["matrix", "Routing Matrix"], ["geo", "Geographic Map"]] as const).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setView(k)}
            className={`rounded-md px-3 py-1.5 font-medium transition ${
              view === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <p className="flex items-start gap-1.5 text-[11px] leading-relaxed text-slate-400">
        <span className="mt-px">ⓘ</span>
        {view === "matrix" ? (
          <span>
            Origin (rows) → destination (columns) hub grid. The diagonal is each hub's <b>local volume</b> (branches it
            sorts itself). An off-diagonal cell shows volume <b>re-homed direct</b> from the origin hub to the
            destination's branches, with the direct <b>transit</b> <code className="rounded bg-slate-100 px-1">H&lt;N&gt;d</code>.
            <b> Click a cell</b> to set branches direct (for a date range).
          </span>
        ) : (
          <span>
            Hubs &amp; branches plotted by location. <b>Hub→branch spokes</b> are sized by each branch's forecast for the
            date; <b>hub↔hub lanes</b> are colored by estimated transit days; <b>reroute lanes</b> (amber) show volume a hub
            event displaces to other hubs. Distance is for display only — routing comes from allocations, branch shares, and events.
          </span>
        )}
      </p>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">{error}</div>}

      {view === "matrix" && (
        <Card className="mb-3">
          <div className="flex flex-wrap items-end gap-5">
            <div className="w-40">
              <Field label="Date">
                <Input type="date" value={date} onChange={(e) => setDate(e.target.value || today())} />
              </Field>
            </div>
            <p className="max-w-md pb-2 text-[11px] leading-relaxed text-slate-400">
              By default every branch is served locally by its home hub. Click a cell and set a branch{" "}
              <b>Direct</b> to re-home it to that origin hub for a date range — its volume leaves the home hub and the
              transit changes to the direct distance.
            </p>
          </div>
        </Card>
      )}

      {view === "matrix" && (
        <Card className="p-0">
          {geoHubs.length < 2 ? (
            <p className="px-5 py-6 text-sm text-slate-500">
              Need at least 2 hubs with latitude/longitude to build the transit matrix.
              {missingCoords > 0 && ` (${missingCoords} hub/branch missing coordinates.)`}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-center text-xs">
                <thead>
                  <tr>
                    <th className="sticky left-0 z-10 bg-white px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      Origin ↓ Dest →
                    </th>
                    {geoHubs.map((h) => (
                      <th key={h.id} className="px-3 py-2 align-bottom" style={{ color: hubColor.get(h.id) ?? "#0f766e" }}>
                        <div className="font-semibold">{h.code || h.name}</div>
                        <div className="text-[10px] font-normal uppercase text-slate-400">{h.city || h.state || ""}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {geoHubs.map((origin) => (
                    <tr key={origin.id} className="border-t border-slate-100">
                      <td
                        className="sticky left-0 z-10 bg-white px-3 py-2 text-left"
                        style={{ color: hubColor.get(origin.id) ?? "#0f766e" }}
                      >
                        <div className="font-semibold">{origin.code || origin.name}</div>
                        <div className="text-[10px] font-normal uppercase text-slate-400">{origin.city || origin.state || ""}</div>
                      </td>
                      {geoHubs.map((dest) => {
                        if (origin.id === dest.id) {
                          const local = Math.round(localVol.get(origin.id) ?? 0);
                          return (
                            <td key={dest.id} className="bg-slate-50 px-3 py-2 text-slate-400" title={`${origin.name} sorts ${fmtN(local)} pkgs locally`}>
                              <div className="text-[10px] uppercase tracking-wide">local</div>
                              <div className="font-medium tabular-nums text-slate-500">{fmtK(local)}</div>
                            </td>
                          );
                        }
                        const t = transitLookup.get(`${origin.id}|${dest.id}`);
                        const col = t ? laneColor(t.d) : "#94a3b8";
                        const k = `${origin.id}|${dest.id}`;
                        const vol = Math.round(directByPair.vol.get(k) ?? 0);
                        const fed = directByPair.cnt.get(k) ?? 0;
                        const dcnt = directByPair.dir.get(k) ?? 0;
                        const nbr = branchesByHub.get(dest.id) ?? 0;
                        return (
                          <td
                            key={dest.id}
                            className="cursor-pointer px-3 py-2 transition hover:bg-brand-50"
                            style={{ backgroundColor: fed > 0 ? `${col}22` : undefined }}
                            onClick={() => setEditPair({ origin, dest })}
                            title={`${origin.name} → ${dest.name}: ${fed > 0 ? `${fmtN(vol)} pkgs, ${fed} branch(es) (${dcnt} direct, ${fed - dcnt} H2H)` : "not fed from here"} · ${t ? `${fmtN(t.miles)} mi · ~${t.d}d` : ""} · click to edit`}
                          >
                            {fed > 0 ? (
                              <>
                                <div className="font-semibold tabular-nums text-emerald-700">{fmtK(vol)}</div>
                                <div className="text-[10px]" style={{ color: col }}>{t ? `H${t.d}d` : "—"}</div>
                                <div className="text-[10px] text-emerald-600">{fed} of {nbr} fed</div>
                              </>
                            ) : (
                              <>
                                <div className="text-[11px] text-slate-300">—</div>
                                <div className="text-[10px]" style={{ color: col }}>{t ? `H${t.d}d` : ""}</div>
                              </>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-4 border-t border-slate-200 px-5 py-2 text-[11px] text-slate-500">
            <span>
              Off-diagonal = <span className="text-emerald-700">direct volume</span> re-homed origin→dest branches ·{" "}
              <code className="rounded bg-slate-100 px-1">H&lt;N&gt;d</code> direct transit. Diagonal = local. Click to edit.
            </span>
            <span>
              Active direct lanes (this date): <span className="font-medium text-slate-700">{activeDirectTotal}</span>
            </span>
          </div>
        </Card>
      )}

      {view === "matrix" && directLanes.length > 0 && (
        <Card className="p-0">
          <div className="border-b border-slate-200 px-5 py-2.5 text-sm font-medium text-slate-700">
            Direct lane records ({directLanes.length})
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-5 py-2 font-medium">Origin → Branch (home hub)</th>
                  <th className="px-3 py-2 font-medium">Routing</th>
                  <th className="px-3 py-2 font-medium">Effective</th>
                  <th className="px-3 py-2 font-medium">Revert</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-5 py-2 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {directLanes
                  .slice()
                  .sort((a, b) => (b.effectiveDate ?? "").localeCompare(a.effectiveDate ?? ""))
                  .map((l) => {
                    const o = hubs.find((h) => h.id === l.originHubId);
                    const b = branches.find((x) => x.id === l.branchId);
                    const home = hubs.find((h) => h.id === b?.hubId);
                    const activeNow = isDirectActiveOnDate(l, date);
                    const scheduled = !!l.effectiveDate && date < l.effectiveDate;
                    const expired = !!l.endDate && date > l.endDate;
                    const status = !l.active ? "Off" : activeNow ? "Active" : scheduled ? "Scheduled" : expired ? "Reverted" : "Inactive";
                    const statusCls = status === "Active" ? "text-emerald-600" : status === "Scheduled" ? "text-sky-600" : "text-slate-400";
                    return (
                      <tr key={l.id} className="border-b border-slate-100 last:border-0">
                        <td className="px-5 py-2">
                          <span className="font-medium text-slate-800">{o?.code || o?.name || "?"}</span>
                          <span className="text-slate-400"> → </span>
                          <span className="text-slate-700">{b?.name || "?"}</span>
                          <span className="text-[11px] text-slate-400"> ({home?.code || home?.name || "?"})</span>
                        </td>
                        <td className="px-3 py-2">
                          <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${l.routing === "h2h" ? "bg-sky-50 text-sky-700" : "bg-emerald-50 text-emerald-700"}`}>
                            {l.routing === "h2h" ? "H2H" : "Direct"}
                          </span>
                        </td>
                        <td className="px-3 py-2 tabular-nums text-slate-600">{l.effectiveDate || "—"}</td>
                        <td className="px-3 py-2 tabular-nums text-slate-600">{l.endDate || "ongoing"}</td>
                        <td className={`px-3 py-2 text-xs font-semibold ${statusCls}`}>{status}</td>
                        <td className="px-5 py-2 text-right">
                          <div className="flex justify-end gap-2">
                            {!expired && l.active && (
                              <button
                                onClick={async () => {
                                  if (l.id) await revertDirectLane(accountId, l.id, today());
                                  reloadDirectLanes();
                                }}
                                className="text-xs font-medium text-amber-600 hover:text-amber-700"
                              >
                                Revert now
                              </button>
                            )}
                            <button
                              onClick={async () => {
                                if (l.id) await deleteDirectLane(accountId, l.id);
                                reloadDirectLanes();
                              }}
                              className="text-xs font-medium text-slate-400 hover:text-red-600"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {view === "geo" && (
      <>
      <Card>
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-52">
            <Field label="Hub focus">
              <Select value={hubId} onChange={(e) => setHubId(e.target.value)}>
                <option value={ALL}>All hubs — full network</option>
                {hubs.map((h) => (
                  <option key={h.id} value={h.id}>
                    {h.name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <div className="w-40">
            <Field label="Date">
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value || today())} />
            </Field>
          </div>
          <div className="flex items-center gap-3 pb-2 text-xs text-slate-600">
            {(["spokes", "lanes", "direct", "reroute"] as const).map((k) => (
              <label key={k} className="flex items-center gap-1.5">
                <input type="checkbox" checked={show[k]} onChange={(e) => setShow((s) => ({ ...s, [k]: e.target.checked }))} />
                {k === "spokes" ? "Branch spokes" : k === "lanes" ? "Hub lanes" : k === "direct" ? "Direct lanes" : "Reroute"}
              </label>
            ))}
          </div>
        </div>
      </Card>

      <Card className="p-0">
        <div className="relative">
          <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="Routing map">
            <rect x={0} y={0} width={W} height={H} fill="#eff6ff" />
            {/* faint lat/lng grid */}
            {[30, 35, 40, 45].map((lat) => {
              const [, y] = toXY(lat, -100);
              return <line key={`la${lat}`} x1={0} y1={y} x2={W} y2={y} stroke="#dbeafe" />;
            })}
            {[-120, -110, -100, -90, -80, -70].map((lng) => {
              const [x] = toXY(38, lng);
              return <line key={`lo${lng}`} x1={x} y1={0} x2={x} y2={H} stroke="#dbeafe" />;
            })}

            {/* hub ↔ hub transit lanes */}
            {show.lanes &&
              transitLanes.map((l, i) => {
                const active = involves(l.a.id, l.b.id);
                return (
                  <line
                    key={i}
                    x1={l.x1}
                    y1={l.y1}
                    x2={l.x2}
                    y2={l.y2}
                    stroke={laneColor(l.d)}
                    strokeWidth={active ? 1.5 : 1}
                    strokeDasharray={l.d <= 1 ? "none" : "5 4"}
                    opacity={active ? 0.7 : 0.12}
                  >
                    <title>{`${l.a.name} ↔ ${l.b.name}: ${fmtN(l.miles)} mi · ~${l.d}d`}</title>
                  </line>
                );
              })}

            {/* h2h lanes (sky dashed, origin hub → destination home hub) */}
            {show.direct &&
              h2hPairs.map(([oid, did], i) => {
                const a = hubXY(oid);
                const b = hubXY(did);
                if (!a || !b) return null;
                return (
                  <line key={`h2h${i}`} x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} stroke="#0ea5e9" strokeWidth={1.5} strokeDasharray="6 4" opacity={involves(oid, did) ? 0.8 : 0.15}>
                    <title>H2H lane</title>
                  </line>
                );
              })}

            {/* hub → branch spokes (direct branches bypass the home hub → no local spoke) */}
            {show.spokes &&
              geoBranches.map((b) => {
                if (branchDirectSet.has(b.id)) return null; // direct → drawn as the green direct line
                const hp = hubXY(b.hubId);
                if (!hp) return null;
                const [bx, by] = toXY(b.lat!, b.lng!);
                const vol = branchVol.get(b.id) ?? 0;
                const col = hubColor.get(b.hubId) ?? "#94a3b8";
                return (
                  <line
                    key={b.id}
                    x1={hp[0]}
                    y1={hp[1]}
                    x2={bx}
                    y2={by}
                    stroke={col}
                    strokeWidth={1 + (vol / maxBranchVol) * 5}
                    opacity={dim(b.hubId) === 1 ? 0.5 : 0.12}
                  >
                    <title>{`${b.name}: ${fmtN(vol)} pkgs`}</title>
                  </line>
                );
              })}

            {/* direct lanes (solid green, origin hub → branch, bypassing dest hub) */}
            {show.direct &&
              geoBranches.map((b) => {
                const origins = geoHubs.filter((o) => directSet.has(`${o.id}|${b.id}`));
                if (!origins.length) return null;
                const [bx, by] = toXY(b.lat!, b.lng!);
                return origins.map((o) => {
                  const op = toXY(o.lat!, o.lng!);
                  return (
                    <line key={`dl${o.id}-${b.id}`} x1={op[0]} y1={op[1]} x2={bx} y2={by} stroke="#059669" strokeWidth={1.5} opacity={involves(o.id, b.hubId) ? 0.8 : 0.15}>
                      <title>{`Direct: ${o.code || o.name} → ${b.name}`}</title>
                    </line>
                  );
                });
              })}

            {/* reroute lanes (amber, bold) */}
            {show.reroute &&
              rerouteLanes.map((l, i) => {
                const a = hubXY(l.from);
                const b = hubXY(l.to);
                if (!a || !b) return null;
                return (
                  <g key={`rr${i}`} opacity={hubId === ALL || hubId === l.from || hubId === l.to ? 1 : 0.2}>
                    <line x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} stroke="#f59e0b" strokeWidth={2 + (l.vol / maxReroute) * 6} opacity={0.85}>
                      <title>{`Reroute ${fmtN(l.vol)} pkgs`}</title>
                    </line>
                    <circle cx={(a[0] + b[0]) / 2} cy={(a[1] + b[1]) / 2} r={3} fill="#f59e0b" stroke="#fff" strokeWidth={1} />
                  </g>
                );
              })}

            {/* branch dots */}
            {geoBranches.map((b) => {
              const [bx, by] = toXY(b.lat!, b.lng!);
              return <circle key={b.id} cx={bx} cy={by} r={4} fill={hubColor.get(b.hubId) ?? "#94a3b8"} stroke="#fff" strokeWidth={1} opacity={dim(b.hubId) * 0.7}>
                <title>{`${b.name} · ${fmtN(branchVol.get(b.id) ?? 0)} pkgs`}</title>
              </circle>;
            })}

            {/* hub nodes */}
            {geoHubs.map((h) => {
              const [x, y] = toXY(h.lat!, h.lng!);
              const col = hubColor.get(h.id) ?? "#0f766e";
              const r = 6 + ((hubVol.get(h.id) ?? 0) / maxHubVol) * 7;
              const o = dim(h.id);
              return (
                <g key={h.id} opacity={o}>
                  <circle cx={x} cy={y} r={r + 5} fill={col} opacity={0.15} />
                  <circle cx={x} cy={y} r={r} fill={col} opacity={0.9} stroke="#fff" strokeWidth={2}>
                    <title>{`${h.name} · ${fmtN(hubVol.get(h.id) ?? 0)} pkgs/day`}</title>
                  </circle>
                  <text x={x} y={y + 1} textAnchor="middle" dominantBaseline="middle" className="fill-white text-[7px] font-bold" style={{ fontFamily: "monospace" }}>
                    {h.code || h.state || ""}
                  </text>
                  <text x={x} y={y + r + 10} textAnchor="middle" className="text-[8px] font-semibold" style={{ fill: col, fontFamily: "monospace" }}>
                    {h.city || h.name}
                  </text>
                </g>
              );
            })}
          </svg>

          {/* legend */}
          <div className="pointer-events-none absolute right-3 top-3 space-y-1 rounded-lg border border-slate-200 bg-white/90 px-3 py-2 text-[10px] text-slate-500 shadow-sm">
            <div className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full bg-slate-400" /> hub / branch</div>
            <div className="flex items-center gap-1.5"><span className="inline-block h-0.5 w-4" style={{ backgroundColor: "#059669" }} /> 1-day lane</div>
            <div className="flex items-center gap-1.5"><span className="inline-block h-0.5 w-4" style={{ backgroundColor: "#0d9488" }} /> 2–3 day</div>
            <div className="flex items-center gap-1.5"><span className="inline-block h-0.5 w-4" style={{ backgroundColor: "#dc2626" }} /> 4+ day</div>
            <div className="flex items-center gap-1.5"><span className="inline-block h-0.5 w-4" style={{ backgroundColor: "#059669" }} /> direct lane</div>
            <div className="flex items-center gap-1.5"><span className="inline-block h-1 w-4" style={{ backgroundColor: "#f59e0b" }} /> reroute flow</div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4 border-t border-slate-200 px-5 py-2 text-xs text-slate-500">
          <span>Branch volume (date): <span className="font-medium text-slate-700">{fmtN(totalBranchVol)}</span></span>
          <span>Reroute lanes: <span className="font-medium text-slate-700">{rerouteLanes.length}</span></span>
          {missingCoords > 0 && <span className="text-amber-600">⚠ {missingCoords} hub/branch missing lat/lng (not plotted)</span>}
        </div>
      </Card>
      </>
      )}

      {editPair && (
        <LaneEditorModal
          accountId={accountId}
          origin={editPair.origin}
          dest={editPair.dest}
          destBranches={destBranchesOf.get(editPair.dest.id) ?? []}
          directLanes={directLanes}
          autoTransit={transitLookup.get(`${editPair.origin.id}|${editPair.dest.id}`)?.d ?? 0}
          onClose={() => setEditPair(null)}
          onSaved={() => {
            setEditPair(null);
            reloadDirectLanes();
          }}
        />
      )}
    </div>
  );
}
